import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Users, Zap, Database, CheckCircle2 } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const features = [
  { icon: Target, title: "AI Lead Sourcing", description: "Automatically discover prospects matching your ICP." },
  { icon: Zap, title: "Intent-Based Prospecting", description: "Target leads showing buying intent signals." },
  { icon: Users, title: "Automated ICP Modeling", description: "AI learns and refines your ideal customer profile." },
  { icon: Database, title: "Dynamic Prioritization", description: "Leads ranked by conversion probability in real-time." },
];

export default function AILeadGeneration() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="AI Lead Generation Engine | Aiveliq360 Autonomous Prospecting"
        description="Aiveliq360 automatically finds, scores, and prioritizes high-intent prospects using AI-powered ICP modeling, intent signals, and behavioral analytics."
      />

      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/auth"><Button size="sm" className="gap-1.5">Get Started<ArrowRight className="h-3.5 w-3.5" /></Button></Link>
          </div>
        </div>
      </header>

      <section className="relative py-16 lg:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6">
            AI Lead Generation Engine:{" "}
            <span className="gradient-text">Find Your Best Prospects Instantly</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Stop wasting time on cold outreach. Let AI find, score, and prioritize 
            high-intent leads automatically.
          </p>
          <Link to="/auth">
            <Button size="lg" className="gap-2 ai-glow">
              Start Free Trial<ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <article key={feature.title} className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10"><feature.icon className="h-5 w-5 text-primary" /></div>
                  <h3 className="font-semibold">{feature.title}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
          <p>© 2025 Aiveliq360</p>
        </div>
      </footer>
    </div>
  );
}
